import { createContext, useContext, useId, useLayoutEffect, useRef } from 'react';
import type { Blocker, BlockerFunction } from 'react-router-dom';
import type { NavigationBlockerApi, NavigationBlockerOptions } from 'types/navigation';

export type { NavigationGuard } from 'types/navigation';

export const idleBlocker: Blocker = { state: 'unblocked', location: undefined, proceed: undefined, reset: undefined };
export const NavigationBlockerContext = createContext<NavigationBlockerApi | null>(null);

export const useNavigationBlockerState = () => {
  const context = useContext(NavigationBlockerContext);
  if (!context) throw new Error('Navigation blockers require NavigationBlockerProvider');
  return context;
};

export const useNavigationBlocker = (
  shouldBlock: boolean | BlockerFunction,
  { priority = 0, group = 'page' }: NavigationBlockerOptions = {}
): Blocker => {
  const { register, unregister, getBlocker } = useNavigationBlockerState();
  const id = useId();
  const predicate = useRef(shouldBlock);
  predicate.current = shouldBlock;
  useLayoutEffect(() => {
    register({
      id,
      priority,
      group,
      shouldBlock: (args) => (typeof predicate.current === 'function' ? predicate.current(args) : predicate.current),
    });
    return () => unregister(id);
  }, [id, priority, group, register, unregister]);
  return getBlocker(id);
};
